import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

// Custom hook for managing dynamic organization colors
export const useOrganizationColors = () => {
  const { user } = useAuth();
  const [colors, setColors] = useState({
    primary: '#FFD700',
    primaryHover: '#E6C200',
    primaryText: '#000000'
  });

  useEffect(() => {
    if (user?.organization?.primary_color) {
      const primaryColor = user.organization.primary_color;
      
      // Update CSS custom properties on document root
      const root = document.documentElement;
      root.style.setProperty('--primary-color', primaryColor);
      root.style.setProperty('--primary-hover-color', adjustBrightness(primaryColor, -20));
      root.style.setProperty('--primary-text-color', getContrastColor(primaryColor));
      
      // Update state for components that need it
      setColors({
        primary: primaryColor,
        primaryHover: adjustBrightness(primaryColor, -20),
        primaryText: getContrastColor(primaryColor)
      });

      console.log('Organization colors updated:', {
        primary: primaryColor,
        hover: adjustBrightness(primaryColor, -20),
        text: getContrastColor(primaryColor)
      });
    }
  }, [user?.organization?.primary_color]);

  return colors;
};

// Helper functions
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

const isLightColor = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5;
};

const getContrastColor = (hex) => {
  return isLightColor(hex) ? '#000000' : '#FFFFFF';
};

const adjustBrightness = (hex, amount) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const r = Math.max(0, Math.min(255, rgb.r + amount));
  const g = Math.max(0, Math.min(255, rgb.g + amount));
  const b = Math.max(0, Math.min(255, rgb.b + amount));
  
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

// Component styles using CSS variables
export const getDynamicButtonStyles = (variant = 'primary') => {
  switch (variant) {
    case 'primary':
      return {
        backgroundColor: 'var(--primary-color, #FFD700)',
        color: 'var(--primary-text-color, #000000)',
        borderColor: 'var(--primary-color, #FFD700)',
        transition: 'all 0.2s ease-in-out'
      };
    case 'primaryHover':
      return {
        backgroundColor: 'var(--primary-hover-color, #E6C200)',
        color: 'var(--primary-text-color, #000000)',
        borderColor: 'var(--primary-hover-color, #E6C200)',
      };
    default:
      return {};
  }
};

export const getDynamicTextStyles = () => ({
  color: 'var(--primary-color, #FFD700)'
});

export default useOrganizationColors;